import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Package } from "lucide-react";

export function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const codOrder = sp.get("order");
  const kind = sp.get("kind");
  const { clear } = useCart();
  const { refresh } = useAuth();
  const [status, setStatus] = useState(codOrder ? "paid" : "checking");
  const [orderNo, setOrderNo] = useState(codOrder || null);
  const isMembership = kind === "membership";
  const isDnReward = kind === "dn_reward";
  const isTravel = kind === "travel";
  const travelRef = sp.get("ref");

  useEffect(() => {
    if (codOrder) { clear(); return; }
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderNo(data.order_no);
        if (data.payment_status === "paid") { setStatus("paid"); if (!isMembership && !isDnReward && !isTravel) clear(); refresh(); return; }
        if (data.status === "expired" || tries > 10) { setStatus("error"); return; }
      } catch { if (tries > 10) { setStatus("error"); return; } }
      tries++; setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId, codOrder]);

  if (isTravel) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center" data-testid="travel-payment-result">
        {status === "checking" && <><Loader2 size={48} className="mx-auto animate-spin text-emerald mb-4" /><h1 className="font-display text-2xl font-bold">Confirming your reservation with the property…</h1><p className="text-sm text-muted-foreground mt-2">Payment received — we're waiting for the booking source to confirm. This usually takes a few seconds.</p></>}
        {status === "paid" && (<><CheckCircle2 size={64} className="mx-auto text-emerald mb-4" /><h1 className="font-display text-3xl font-bold">Payment received</h1><p className="text-muted-foreground mt-2">Booking {travelRef}. Your confirmation status and itinerary are in My Trips.</p><Link to={`/travel/trips/${travelRef}`} className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold" data-testid="view-trip-btn">View my trip</Link></>)}
        {status === "error" && (<><XCircle size={64} className="mx-auto text-coral mb-4" /><h1 className="font-display text-2xl font-bold">We couldn't confirm the payment yet</h1><p className="text-sm text-muted-foreground mt-2">If your card was charged, your booking will confirm automatically — check My Trips in a few minutes.</p><Link to={`/travel/trips/${travelRef}`} className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Check booking</Link></>)}
      </div>
    );
  }

  if (isMembership) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        {status === "checking" && <><Loader2 size={48} className="mx-auto animate-spin text-emerald mb-4" /><h1 className="font-display text-2xl font-bold">Activating your membership…</h1></>}
        {status === "paid" && (
          <>
            <CheckCircle2 size={64} className="mx-auto text-gold mb-4" />
            <h1 className="font-display text-3xl font-bold">Welcome to SHOP+! 👑</h1>
            <p className="text-muted-foreground mt-2">Your membership is active. Enjoy free delivery, early deals, bonus points and priority support.</p>
            <Link to="/account" className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Go to My Account</Link>
          </>
        )}
        {status === "error" && (<><XCircle size={64} className="mx-auto text-coral mb-4" /><h1 className="font-display text-2xl font-bold">Couldn't confirm membership</h1><Link to="/shop-plus" className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Try Again</Link></>)}
      </div>
    );
  }

  if (isDnReward) {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        {status === "checking" && <><Loader2 size={48} className="mx-auto animate-spin text-emerald mb-4" /><h1 className="font-display text-2xl font-bold">Securing your reward…</h1></>}
        {status === "paid" && (
          <>
            <CheckCircle2 size={64} className="mx-auto text-emerald mb-4" />
            <h1 className="font-display text-3xl font-bold">Reward secured in escrow 🔒</h1>
            <p className="text-muted-foreground mt-2">Your courier's reward is now protected by SHOP NOW and released automatically once the package is delivered.</p>
            <Link to="/delivery-network" className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Back to Delivery Network</Link>
          </>
        )}
        {status === "error" && (<><XCircle size={64} className="mx-auto text-coral mb-4" /><h1 className="font-display text-2xl font-bold">Couldn't confirm the reward</h1><Link to="/delivery-network" className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Try Again</Link></>)}
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      {status === "checking" && <><Loader2 size={48} className="mx-auto animate-spin text-emerald mb-4" /><h1 className="font-display text-2xl font-bold">Confirming your payment…</h1></>}
      {status === "paid" && (
        <>
          <CheckCircle2 size={64} className="mx-auto text-emerald mb-4" />
          <h1 className="font-display text-3xl font-bold">Order Confirmed!</h1>
          <p className="text-muted-foreground mt-2">Thank you for shopping with SHOP NOW. {orderNo && <>Your order <span className="font-mono-sn font-semibold text-ink">{orderNo}</span> is being prepared.</>}</p>
          <div className="flex gap-3 justify-center mt-6">
            {orderNo && <Link to={`/account`} className="bg-ink text-white rounded-full px-6 py-3 font-semibold flex items-center gap-2"><Package size={18} /> Track Order</Link>}
            <Link to="/" className="border border-border rounded-full px-6 py-3 font-semibold">Continue Shopping</Link>
          </div>
        </>
      )}
      {status === "error" && (
        <>
          <XCircle size={64} className="mx-auto text-coral mb-4" />
          <h1 className="font-display text-2xl font-bold">We couldn't confirm payment</h1>
          <p className="text-muted-foreground mt-2">If you were charged, please contact support with your order details.</p>
          <Link to="/support" className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Contact Support</Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <XCircle size={64} className="mx-auto text-coral mb-4" />
      <h1 className="font-display text-2xl font-bold">Payment Cancelled</h1>
      <p className="text-muted-foreground mt-2">Your cart is still saved. You can complete checkout whenever you're ready.</p>
      <Link to="/checkout" className="inline-block mt-6 bg-ink text-white rounded-full px-6 py-3 font-semibold">Return to Checkout</Link>
    </div>
  );
}
